import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useMemo, useRef, useState } from "react";
import {
  Inbox,
  RefreshCw,
  Check,
  X as XIcon,
  Clock,
  CheckCircle2,
  XCircle,
  Funnel,
  FileSpreadsheet,
  Loader2 } from
"lucide-react";
import { format, parseISO } from "date-fns";
import { cn } from "../../../lib/utils";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { EmptyState, LoadingState, PageHeader, ui } from "../../../components/ui";
import {
  ApproveModal,
  RejectModal,
  LeaveActionToast,
  LeaveStatusBadge,
  ManagerLeaveFiltersModal,
  ManagerLeaveMobileList,
  ManagerLeaveRequestDetailsModal,
  TablePagination,
  countActiveFilters,
  getDefaultLeaveFilters } from
"../components";
import { leaveService, getLeaveDuration } from "../../../services/leaveService";
import { getManagerReportees } from "../../../services/employmentService";
import { extractLeaveRecords, normalizeStatus, getLeaveSessionFields, getLeaveRecordEmpId } from "../../../lib/leaveRecords";
import sessionDB from "../../../utils/sessionDB";
import { getLeaveApiErrorMessage, buildSuccessToast } from "../../../lib/leaveApiMessages";

function getInitialsFromName(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }
  return parts[0]?.slice(0, 2).toUpperCase() || "?";
}

function getSessionEmployeeInfo() {
  const emp = sessionDB.getEmpObj();
  const empId = Number(emp.emp_id);
  const name = [emp.first_name, emp.last_name].filter(Boolean).join(" ").trim();

  return {
    empId,
    name: name || (Number.isFinite(empId) ? `Employee #${empId}` : "Employee"),
    initials: getInitialsFromName(name)
  };
}

export function ManagerLeaveRequestsPage() {
  const PAGE_SIZE = 10;
  const [rows, setRows] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(false);
  const [leaveTypesError, setLeaveTypesError] = useState("");
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(getDefaultLeaveFilters);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);

  const [reportees, setReportees] = useState([]);
  const [reporteesLoading, setReporteesLoading] = useState(false);
  const [reporteesError, setReporteesError] = useState("");

  const [approveRequest, setApproveRequest] = useState(null);
  const [rejectRequest, setRejectRequest] = useState(null);
  const [approveError, setApproveError] = useState("");
  const [rejectError, setRejectError] = useState("");
  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const tableRef = useRef(null);

  const { employee, leaveType, status, dateRange } = filters;

  const safeFormatDate = (value) => {
    if (!value) return "—";
    try {
      return format(parseISO(value), "dd MMM yyyy");
    } catch {
      return "—";
    }
  };

  const getLeaveTypeLabel = (request) => {
    if (request?.leaveTypeName) return request.leaveTypeName;
    const matchedType = leaveTypes.find((type) => type.typeId === request?.leaveTypeId);
    if (matchedType?.leaveName) return matchedType.leaveName;
    if (request?.leaveTypeCode) return request.leaveTypeCode;
    return "Leave";
  };

  const resolveEmployeeInfo = (empId, request) => {
    const reportee = reportees.find((item) => Number(item.id) === Number(empId));
    if (reportee) {
      return {
        empId: Number(empId),
        name: reportee.name,
        initials: reportee.initials
      };
    }

    const sessionEmpId = Number(sessionDB.getEmpId());
    if (Number(empId) === sessionEmpId) {
      return getSessionEmployeeInfo();
    }

    const nameFromRecord =
    request?.empName ||
    request?.employeeName ||
    request?.fullName ||
    [request?.firstName, request?.lastName].filter(Boolean).join(" ").trim();

    if (nameFromRecord) {
      return {
        empId: Number(empId),
        name: nameFromRecord,
        initials: getInitialsFromName(nameFromRecord)
      };
    }

    return {
      empId: Number(empId),
      name: `Employee #${empId}`,
      initials: "?"
    };
  };

  const loadRequests = async () => {
    setLoading(true);
    try {
      const sessionManagerId = Number(sessionDB.getEmpId());
      const requestOptions = {
        page: 0,
        size: 10,
        ...(Number.isFinite(sessionManagerId) && sessionManagerId > 0 ?
        { managerId: sessionManagerId } :
        {})
      };

      const [pendingResponse, approvedResponse, rejectedResponse] = await Promise.all([
      leaveService.getLeavesByStatus("Pending", requestOptions),
      leaveService.getLeavesByStatus("Approved", requestOptions),
      leaveService.getLeavesByStatus("Rejected", requestOptions)]
      );

      const pendingList = extractLeaveRecords(pendingResponse);
      const approvedList = extractLeaveRecords(approvedResponse);
      const rejectedList = extractLeaveRecords(rejectedResponse);
      const all = [...pendingList, ...approvedList, ...rejectedList];

      all.sort((a, b) => Number(b.leaveId) - Number(a.leaveId));
      setRows(
        all.map((record) => ({
          ...record,
          empId: getLeaveRecordEmpId(record) ?? record.empId,
        }))
      );
    } catch (err) {
      console.error("Failed to load requests from API", err);
    } finally {
      setLoading(false);
    }
  };

  const loadReportees = async () => {
    setReporteesLoading(true);
    setReporteesError("");
    try {
      const managerId = Number(sessionDB.getEmpId());
      if (!Number.isFinite(managerId) || managerId <= 0) {
        setReportees([]);
        setReporteesError("Unable to determine manager from login session.");
        return;
      }

      const list = await getManagerReportees(managerId, { page: 0, size: 10 });
      setReportees(list);
    } catch (err) {
      console.error("Failed to load reportees", err);
      setReportees([]);
      setReporteesError(
        err?.message || "Unable to load employees. Please try again."
      );
    } finally {
      setReporteesLoading(false);
    }
  };

  const loadLeaveTypes = async () => {
    setLeaveTypesLoading(true);
    setLeaveTypesError("");
    try {
      const types = await leaveService.getLeaveTypes();
      setLeaveTypes(Array.isArray(types) ? types : []);
    } catch (err) {
      console.error("Failed to load leave types", err);
      setLeaveTypes([]);
      setLeaveTypesError(
        err?.message || "Unable to load leave types. Please try again."
      );
    } finally {
      setLeaveTypesLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    loadLeaveTypes();
  }, []);

  // Filter dropdown APIs — only when the filter modal opens
  useEffect(() => {
    if (!filterModalOpen) return undefined;

    loadReportees();
    loadLeaveTypes();
    return undefined;
  }, [filterModalOpen]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const reporteeIds = useMemo(
    () => new Set(reportees.map((reportee) => Number(reportee.id))),
    [reportees]
  );

  const reporteeRows = useMemo(() => {
    if (reporteesLoading) return rows;
    if (reporteeIds.size > 0) {
      return rows.filter((row) => reporteeIds.has(Number(row.empId)));
    }
    return rows;
  }, [rows, reporteeIds, reporteesLoading]);

  const counts = useMemo(
    () => ({
      pending: reporteeRows.filter((row) => row.leaveStatus?.toUpperCase() === "PENDING").length,
      approved: reporteeRows.filter((row) => row.leaveStatus?.toUpperCase() === "APPROVED").length,
      rejected: reporteeRows.filter((row) => row.leaveStatus?.toUpperCase() === "REJECTED").length
    }),
    [reporteeRows]
  );

  const buildModalRequest = (rawRequest) => {
    const { fromSession, toSession, session } = getLeaveSessionFields(rawRequest);

    return {
      id: String(rawRequest.leaveId),
      leaveId: rawRequest.leaveId,
      empId: getLeaveRecordEmpId(rawRequest) ?? rawRequest.empId,
      leaveTypeId: rawRequest.leaveTypeId,
      employee: resolveEmployeeInfo(rawRequest.empId, rawRequest).name,
      type: getLeaveTypeLabel(rawRequest),
      from: safeFormatDate(rawRequest.startDate),
      to: safeFormatDate(rawRequest.endDate),
      days: getLeaveDuration(rawRequest),
      appliedOn: safeFormatDate(rawRequest.createdAt || rawRequest.createdOn),
      status: normalizeStatus(rawRequest.leaveStatus),
      reason: rawRequest.leaveReason || rawRequest.reason || "",
      actionByRemarks: rawRequest.actionByRemarks,
      fromSession,
      toSession,
      session
    };
  };

  const visible = useMemo(() => {
    let out = [...reporteeRows];
    if (employee !== "all") {
      out = out.filter((r) => r.empId === Number(employee));
    }
    if (leaveType !== "all") {
      out = out.filter((r) => String(r.leaveTypeId) === String(leaveType));
    }
    if (status !== "all") {
      out = out.filter((r) => r.leaveStatus?.toLowerCase() === status.toLowerCase());
    }

    if (dateRange?.from) {
      out = out.filter((r) => {
        const reqFrom = parseISO(r.startDate);
        return reqFrom >= dateRange.from;
      });
    }
    if (dateRange?.to) {
      out = out.filter((r) => {
        const reqTo = parseISO(r.endDate);
        return reqTo <= dateRange.to;
      });
    }
    return out;
  }, [reporteeRows, employee, leaveType, status, dateRange]);

  const activeFilterCount = countActiveFilters(filters);

  const handleReset = () => {
    setFilters(getDefaultLeaveFilters());
    setPage(1);
  };

  const handleExport = async () => {
    if (exporting || activeFilterCount === 0) return;

    setExporting(true);
    try {
      await leaveService.exportTeamLeaveRequests({
        fromDate: dateRange?.from,
        toDate: dateRange?.to,
        requestType: "Team"
      });
      setToast({ message: "Leave requests exported successfully.", type: "success" });
    } catch (err) {
      console.error("Failed to export team leave requests", err);
      const message = getLeaveApiErrorMessage(err);
      setToast({
        message: message || "Unable to export leave requests. Please try again.",
        type: "error"
      });
    } finally {
      setExporting(false);
    }
  };

  const handleApplyFilters = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleStatusWidgetClick = (statusValue) => {
    setFilters((prev) => ({
      ...prev,
      status: prev.status === statusValue ? "all" : statusValue
    }));
    requestAnimationFrame(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pagedVisible = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return visible.slice(start, start + PAGE_SIZE);
  }, [visible, page, PAGE_SIZE]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [employee, leaveType, status, dateRange.from, dateRange.to]);

  const submitApproval = async (remarks, request = approveRequest) => {
    if (!request || loading) return;
    setApproveError("");
    try {
      setLoading(true);
      const response = await leaveService.approveLeaveRequest(request.leaveId, remarks);
      setApproveRequest(null);
      setSelectedRequest(null);
      const successToast = buildSuccessToast(response, { includeRemarks: false });
      if (successToast) setToast(successToast);
      await loadRequests();
    } catch (err) {
      console.error("Failed to approve leave request", err);
      const message = getLeaveApiErrorMessage(err);
      if (message) setApproveError(message);
    } finally {
      setLoading(false);
    }
  };

  const submitRejection = async (remarks, request = rejectRequest) => {
    if (!request || loading) return;
    setRejectError("");
    try {
      setLoading(true);
      const response = await leaveService.rejectLeaveRequest(request.leaveId, remarks);
      setRejectRequest(null);
      setSelectedRequest(null);
      const successToast = buildSuccessToast(response, { includeRemarks: false });
      if (successToast) setToast(successToast);
      await loadRequests();
    } catch (err) {
      console.error("Failed to reject leave request", err);
      const message = getLeaveApiErrorMessage(err);
      if (message) setRejectError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout
      overlay={
      <>
          <ManagerLeaveFiltersModal
          open={filterModalOpen}
          onOpenChange={setFilterModalOpen}
          appliedFilters={filters}
          onApply={handleApplyFilters}
          employees={reportees}
          leaveTypes={leaveTypes}
          employeesLoading={reporteesLoading}
          employeesError={reporteesError}
          leaveTypesLoading={leaveTypesLoading}
          leaveTypesError={leaveTypesError} />
        

          <ApproveModal
          open={!!approveRequest}
          onOpenChange={(open) => {
            if (!open && !loading) {
              setApproveRequest(null);
              setApproveError("");
            }
          }}
          request={approveRequest ? buildModalRequest(approveRequest) : null}
          onConfirm={(remarks) => submitApproval(remarks, approveRequest)}
          loading={loading}
          error={approveError} />
        

          <RejectModal
          open={!!rejectRequest}
          onOpenChange={(open) => {
            if (!open && !loading) {
              setRejectRequest(null);
              setRejectError("");
            }
          }}
          request={rejectRequest ? buildModalRequest(rejectRequest) : null}
          onConfirm={(remarks) => submitRejection(remarks, rejectRequest)}
          loading={loading}
          error={rejectError} />
        

          <ManagerLeaveRequestDetailsModal
          open={!!selectedRequest}
          onOpenChange={(open) => {
            if (!open && !loading) {
              setSelectedRequest(null);
              setApproveError("");
              setRejectError("");
            }
          }}
          request={selectedRequest ? buildModalRequest(selectedRequest) : null}
          onApprove={(remarks) => submitApproval(remarks, selectedRequest)}
          onReject={(remarks) => submitRejection(remarks, selectedRequest)}
          loading={loading}
          approveError={approveError}
          rejectError={rejectError} />
        

          <LeaveActionToast toast={toast} />
        </>
      }>
      
          <PageBackLink to="/hrms/manager" />
          <PageHeader
        title={i18n.t("leave.leaveApprovals")} />
      

          <div className="grid grid-cols-3 gap-2 sm:gap-3.5 mb-5">
            <OverviewCard
          title={i18n.t("status.pending")}
          value={counts.pending}
          icon={Clock}
          tone="text-[var(--color-warning)]"
          bg="bg-[var(--color-warning)]/10"
          active={status === "pending"}
          onClick={() => handleStatusWidgetClick("pending")} />
        
            <OverviewCard
          title={i18n.t("status.approved")}
          value={counts.approved}
          icon={CheckCircle2}
          tone="text-[var(--color-success)]"
          bg="bg-[var(--color-success)]/10"
          active={status === "approved"}
          onClick={() => handleStatusWidgetClick("approved")} />
        
            <OverviewCard
          title={i18n.t("status.rejected")}
          value={counts.rejected}
          icon={XCircle}
          tone="text-[var(--color-error)]"
          bg="bg-[var(--color-error)]/10"
          active={status === "rejected"}
          onClick={() => handleStatusWidgetClick("rejected")} />
        
          </div>

          <div
        ref={tableRef}
        id="leave-requests-table"
        className={cn(ui.card, "overflow-hidden scroll-mt-6")}>
        
            <div className="px-4 sm:px-6 py-3 border-b border-[var(--color-text-border)]/70 flex flex-wrap items-center justify-end gap-1.5">
              <button
            type="button"
            onClick={() => setFilterModalOpen(true)}
            className={ui.filterBtn}>
            
                <Funnel className="h-4 w-4 shrink-0" />
                <span><Trans i18nKey="formActions.filter" /></span>
                {activeFilterCount > 0 &&
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-primary)] px-1.5 text-[11px] font-bold text-white">
                    {activeFilterCount}
                  </span>
            }
              </button>

              <button
            type="button"
            onClick={handleExport}
            disabled={exporting || activeFilterCount === 0}
            aria-label={i18n.t("formActions.exportLeaveRequestsToExcel")}
            className={`${ui.filterBtn} disabled:opacity-50 disabled:cursor-not-allowed`}>
            
                {exporting ?
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> :

            <FileSpreadsheet className="h-4 w-4 shrink-0" />
            }
                <span>{exporting ? "Exporting..." : "Export Excel"}</span>
              </button>

              <button
            type="button"
            onClick={handleReset}
            disabled={activeFilterCount === 0}
            className={`${ui.filterBtn} disabled:opacity-50 disabled:cursor-not-allowed`}>
            
                <RefreshCw className="h-4 w-4 shrink-0" />
                <span><Trans i18nKey="formActions.resetFilters" /></span>
              </button>
            </div>

            {loading || reporteesLoading ?
        <LoadingState message="Loading requests queue..." /> :
        visible.length === 0 ?
        <EmptyState
          icon={Inbox}
          title={i18n.t("leave.noLeaveRequestsFound")}
          message="Try adjusting your filters to see more results." /> :


        <>
                <ManagerLeaveMobileList
            rows={pagedVisible}
            resolveEmployeeInfo={resolveEmployeeInfo}
            onSelect={(request) => {
              setApproveError("");
              setRejectError("");
              setSelectedRequest(request);
            }} />
          
                <RequestsTableBody
            rows={pagedVisible}
            leaveTypes={leaveTypes}
            resolveEmployeeInfo={resolveEmployeeInfo}
            onSelect={(request) => {
              setApproveError("");
              setRejectError("");
              setSelectedRequest(request);
            }}
            onApprove={(r) => {
              setApproveError("");
              setApproveRequest(r);
            }}
            onReject={(r) => {
              setRejectError("");
              setRejectRequest(r);
            }} />
          
                <TablePagination
            page={page}
            totalPages={totalPages}
            totalItems={visible.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage} />
          
              </>
        }
          </div>
    </AppLayout>);

}

function OverviewCard({ title, value, icon: Icon, tone, bg, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "w-full text-left bg-white rounded-xl px-2.5 py-2.5 sm:px-4 sm:py-3.5 shadow-[0_10px_24px_-20px_rgba(30,51,131,0.35)] border transition cursor-pointer",
        active ?
        "border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5 ring-2 ring-[var(--color-primary)]/15" :
        "border-white/60 hover:border-[var(--color-primary)]/25 hover:bg-[var(--color-workspace)]/30"
      )}>
      
      <div className="flex items-start sm:items-center justify-between gap-1.5 sm:gap-3">
        <div className="min-w-0">
          <p className="text-[11px] sm:text-[13px] font-medium text-[var(--color-text-secondary)] leading-tight line-clamp-2 sm:truncate">
            {title}
          </p>
          <p className="text-[20px] sm:text-[26px] font-bold text-foreground leading-none mt-1 sm:mt-1.5">{value}</p>
        </div>
        <div className={`h-7 w-7 sm:h-9 sm:w-9 shrink-0 rounded-lg ${bg} grid place-items-center`}>
          <Icon className={`h-3.5 w-3.5 sm:h-[18px] sm:w-[18px] ${tone}`} />
        </div>
      </div>
    </button>);

}

function ActionIcons({ onApprove, onReject }) {
  return (
    <div
      className="inline-flex items-center gap-2"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}>
      
      <button
        type="button"
        onClick={onApprove}
        title={i18n.t("formActions.approve")}
        aria-label={i18n.t("formActions.approveLeaveRequest2")}
        className="inline-grid place-items-center h-8 w-8 rounded-lg border border-[var(--color-success)]/30 text-[var(--color-success)] hover:bg-[var(--color-success)] hover:text-white transition cursor-pointer">
        
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onReject}
        title={i18n.t("formActions.reject")}
        aria-label={i18n.t("formActions.rejectLeaveRequest2")}
        className="inline-grid place-items-center h-8 w-8 rounded-lg border border-[var(--color-error)]/30 text-[var(--color-error)] hover:bg-[var(--color-error)] hover:text-white transition cursor-pointer">
        
        <XIcon className="h-4 w-4" />
      </button>
    </div>);

}

function RequestsTableBody({
  rows,
  leaveTypes,
  resolveEmployeeInfo,
  onSelect,
  onApprove,
  onReject
}) {
  const safeFormatDate = (value) => {
    if (!value) return "—";
    try {
      return format(parseISO(value), "dd MMM yyyy");
    } catch {
      return "—";
    }
  };

  const getLeaveTypeLabel = (request) => {
    if (request?.leaveTypeName) return request.leaveTypeName;
    const matchedType = leaveTypes.find((type) => type.typeId === request?.leaveTypeId);
    if (matchedType?.leaveName) return matchedType.leaveName;
    if (request?.leaveTypeCode) return request.leaveTypeCode;
    return "Leave";
  };

  return (
    <div className="hidden md:block w-full overflow-x-auto">
      <table className="w-full text-sm min-w-[1000px]">
        <thead>
          <tr className={ui.tableHead}>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leaveId" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="common.employee" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leaveType" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.leavePeriod" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="dateTime.days" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="status.status" /></th>
            <th className="px-4 py-3 font-semibold text-right whitespace-nowrap"><Trans i18nKey="common.action" /></th>
            <th className="px-4 py-3 font-semibold whitespace-nowrap"><Trans i18nKey="leave.appliedOn" /></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-text-border)]/35">
          {rows.map((r) => {
            const empInfo = resolveEmployeeInfo(r.empId, r);
            const daysCount = getLeaveDuration(r);
            const typeName = getLeaveTypeLabel(r);
            return (
              <tr
                key={r.leaveId}
                onClick={() => onSelect?.(r)}
                className="hover:bg-[var(--color-workspace)]/20 transition-colors cursor-pointer">
                
                <td className="px-4 py-3.5 font-semibold text-[var(--color-primary)] whitespace-nowrap">#{r.leaveId}</td>
                <td className="px-4 py-3.5 font-semibold text-foreground whitespace-nowrap">{empInfo.name}</td>
                <td className="px-4 py-3.5 font-semibold text-foreground whitespace-nowrap">{typeName}</td>
                <td className="px-4 py-3.5 text-[var(--color-text-secondary)] whitespace-nowrap">
                  {safeFormatDate(r.startDate) === safeFormatDate(r.endDate) ?
                  safeFormatDate(r.startDate) :
                  `${safeFormatDate(r.startDate)} – ${safeFormatDate(r.endDate)}`}
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-[var(--color-workspace)]/60 text-[var(--color-primary)] text-xs font-semibold">
                    {daysCount} {daysCount === 1 ? "Day" : "Days"}
                  </span>
                </td>
                <td className="px-4 py-3.5 whitespace-nowrap">
                  <LeaveStatusBadge status={normalizeStatus(r.leaveStatus)} />
                </td>
                <td className="px-4 py-3.5 text-right whitespace-nowrap">
                  {r.leaveStatus?.toUpperCase() === "PENDING" ?
                  <ActionIcons
                    onApprove={() => onApprove(r)}
                    onReject={() => onReject(r)} /> :


                  <span className="text-[var(--color-text-secondary)]">—</span>
                  }
                </td>
                <td className="px-4 py-3.5 text-[var(--color-text-secondary)] whitespace-nowrap">{safeFormatDate(r.createdAt || r.createdOn || r.startDate)}</td>
              </tr>);

          })}
        </tbody>
      </table>
    </div>);

}
